import React from 'react';
import { 
  AdjustmentsHorizontalIcon,
  PlusIcon,
  ArrowsPointingOutIcon,
  PaperClipIcon,
  FaceSmileIcon,
  PaperAirplaneIcon,
  ArrowDownIcon
} from '@heroicons/react/24/outline';

export default function SupportTickets() {
  return (
    <div className="px-8 py-8 max-w-[1400px] mx-auto flex flex-col gap-6 pb-20">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-2">
        <div>
          <h1 className="text-[32px] font-extrabold text-slate-900 tracking-tight">Support Tickets</h1>
          <p className="text-[15px] text-slate-500 mt-1 font-medium">
            Manage and resolve incoming customer enquiries and technical issues.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="flex items-center gap-2 bg-slate-100 border border-slate-200 text-slate-700 px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-slate-200 transition-colors shadow-sm">
            <AdjustmentsHorizontalIcon className="w-4 h-4 text-slate-500" /> Filters
          </button>
          <button className="flex items-center gap-2 bg-brand-600 text-white px-5 py-2.5 rounded-xl text-sm font-bold hover:bg-brand-700 transition-colors shadow-sm shadow-brand-500/20">
            <PlusIcon className="w-5 h-5" /> New Ticket
          </button>
        </div>
      </div>

      {/* Main Layout Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
        
        {/* Left Column: Ticket List */}
        <div className="xl:col-span-8 bg-white border border-slate-200 rounded-[24px] shadow-sm flex flex-col overflow-hidden">
          
          {/* Table Header */}
          <div className="grid grid-cols-12 px-8 py-5 bg-slate-50/50 border-b border-slate-100 text-[11px] font-extrabold text-slate-400 uppercase tracking-widest">
            <div className="col-span-2">Ticket ID</div>
            <div className="col-span-4">Subject</div>
            <div className="col-span-2">Priority</div>
            <div className="col-span-2">Status</div>
            <div className="col-span-2 text-right">Update</div>
          </div>

          {/* List Body */}
          <div className="flex flex-col divide-y divide-slate-100">
            
            {/* Row 1 */}
            <div className="grid grid-cols-12 items-center px-8 py-6 hover:bg-slate-50 transition-colors cursor-pointer">
              <div className="col-span-2 text-[13px] font-medium text-slate-500">
                #TK-8842
              </div>
              <div className="col-span-4 pr-4">
                <h4 className="text-[14px] font-extrabold text-slate-900 mb-0.5">API Gateway Timeout</h4>
                <p className="text-[12px] text-slate-500">by Sarah Jenkins</p>
              </div>
              <div className="col-span-2">
                <span className="px-3 py-1 bg-red-50 text-red-600 text-[9px] font-extrabold uppercase tracking-widest rounded-full">Urgent</span>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-brand-500"></div>
                <span className="text-[12px] font-bold text-slate-700">In Progress</span>
              </div>
              <div className="col-span-2 text-right text-[12px] text-slate-500 font-medium">
                2 mins ago
              </div>
            </div>

            {/* Row 2 */}
            <div className="grid grid-cols-12 items-center px-8 py-6 hover:bg-slate-50 transition-colors cursor-pointer">
              <div className="col-span-2 text-[13px] font-medium text-slate-500">
                #TK-8839
              </div>
              <div className="col-span-4 pr-4">
                <h4 className="text-[14px] font-extrabold text-slate-900 mb-0.5">Billing Discrepancy</h4>
                <p className="text-[12px] text-slate-500">by Global Dynamics</p>
              </div>
              <div className="col-span-2">
                <span className="px-3 py-1 bg-amber-50 text-amber-600 text-[9px] font-extrabold uppercase tracking-widest rounded-full">High</span>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                <span className="text-[12px] font-bold text-slate-700">Open</span>
              </div>
              <div className="col-span-2 text-right text-[12px] text-slate-500 font-medium">
                14 mins ago
              </div>
            </div>

            {/* Row 3 (Active State) */}
            <div className="grid grid-cols-12 items-center px-8 py-6 bg-brand-50/30 border-l-4 border-brand-500 cursor-pointer">
              <div className="col-span-2 text-[13px] font-extrabold text-brand-600 pl-2">
                #TK-8835
              </div>
              <div className="col-span-4 pr-4">
                <h4 className="text-[14px] font-extrabold text-slate-900 mb-0.5">Custom Domain Setup</h4>
                <p className="text-[12px] text-slate-500">by Marcus Aurelio</p>
              </div>
              <div className="col-span-2">
                <span className="px-3 py-1 bg-purple-100/50 text-purple-700 text-[9px] font-extrabold uppercase tracking-widest rounded-full">Medium</span>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-brand-500"></div>
                <span className="text-[12px] font-bold text-slate-700">In Progress</span>
              </div>
              <div className="col-span-2 text-right text-[12px] text-slate-500 font-medium">
                1 hour ago
              </div>
            </div>

            {/* Row 4 */}
            <div className="grid grid-cols-12 items-center px-8 py-6 hover:bg-slate-50 transition-colors cursor-pointer">
              <div className="col-span-2 text-[13px] font-medium text-slate-500">
                #TK-8831
              </div>
              <div className="col-span-4 pr-4">
                <h4 className="text-[14px] font-extrabold text-slate-900 mb-0.5">Dark Mode Typos</h4>
                <p className="text-[12px] text-slate-500">by Beta Tester #04</p>
              </div>
              <div className="col-span-2">
                <span className="px-3 py-1 bg-slate-100 text-slate-500 text-[9px] font-extrabold uppercase tracking-widest rounded-full">Low</span>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-slate-400"></div>
                <span className="text-[12px] font-bold text-slate-700">Resolved</span>
              </div>
              <div className="col-span-2 text-right text-[12px] text-slate-500 font-medium">
                4 hours ago
              </div>
            </div>

          </div>
        </div>

        {/* Right Column: Quick Reply Panel */}
        <div className="xl:col-span-4 bg-white border border-slate-200 rounded-[24px] shadow-lg shadow-slate-200/50 flex flex-col p-6 relative">
          
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-[18px] font-extrabold text-slate-900 tracking-tight">Quick Reply</h3>
            <button className="text-slate-400 hover:text-slate-900 transition-colors"><ArrowsPointingOutIcon className="w-5 h-5" /></button>
          </div>

          <div className="flex-1 flex flex-col gap-6">
            
            {/* Original Message */}
            <div>
              <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Original Message</p>
              <div className="bg-slate-50 border border-slate-100 rounded-xl p-4">
                <p className="text-[13px] text-slate-700 leading-relaxed font-medium">
                  "I'm trying to link my custom domain (app.mybrand.com) but it's stuck on 'Verifying'. CNAME records are propagated."
                </p>
              </div>
            </div>

            {/* Assignment Buttons */}
            <div className="flex items-center gap-3">
              <button className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-600 py-2.5 rounded-full text-[12px] font-extrabold transition-colors">
                Assign to Me
              </button>
              <button className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-600 py-2.5 rounded-full text-[12px] font-extrabold transition-colors">
                Escalate
              </button>
            </div>

            {/* Internal Note */}
            <div>
              <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Internal Note (Private)</p>
              <textarea 
                rows={2}
                placeholder="Type private note..."
                className="w-full bg-slate-50 border border-slate-100 rounded-xl p-3 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500/20 transition-all resize-none"
              ></textarea>
            </div>

            {/* Reply Area */}
            <div>
              <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest mb-2">Reply to Marcus</p>
              <div className="border border-slate-200 rounded-xl overflow-hidden focus-within:border-brand-300 focus-within:ring-2 focus-within:ring-brand-500/20 transition-all bg-white flex flex-col">
                <textarea 
                  rows={4}
                  placeholder="Write your response..."
                  className="w-full bg-transparent border-none p-4 text-[13px] text-slate-900 placeholder:text-slate-400 focus:outline-none resize-none"
                ></textarea>
                <div className="px-4 py-2 border-t border-slate-100 flex justify-end gap-3 bg-slate-50/50">
                  <button className="text-slate-400 hover:text-slate-600 transition-colors"><PaperClipIcon className="w-5 h-5" /></button>
                  <button className="text-slate-400 hover:text-slate-600 transition-colors"><FaceSmileIcon className="w-5 h-5" /></button>
                </div>
              </div>
            </div>

            <div className="mt-auto">
              <button className="w-full bg-brand-600 text-white py-3.5 rounded-xl text-[14px] font-bold flex items-center justify-center gap-2 hover:bg-brand-700 transition-colors shadow-sm">
                Send Reply <PaperAirplaneIcon className="w-4 h-4" />
              </button>
              <p className="text-center text-[10px] font-medium text-slate-400 mt-3">
                Shortcut: Press <kbd className="bg-slate-100 border border-slate-200 px-1 py-0.5 rounded text-[9px] mx-0.5">Cmd</kbd> + <kbd className="bg-slate-100 border border-slate-200 px-1 py-0.5 rounded text-[9px] mx-0.5">Enter</kbd>
              </p>
            </div>

          </div>
        </div>

      </div>

      {/* Bottom Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mt-2">
        
        {/* Avg Response Time */}
        <div className="bg-slate-50 border border-slate-100 rounded-[20px] p-6 shadow-sm">
          <p className="text-[11px] font-extrabold text-slate-500 uppercase tracking-widest mb-3">Avg Response Time</p>
          <div className="flex items-end gap-3">
            <h2 className="text-[36px] font-extrabold text-slate-900 tracking-tight leading-none">14m</h2>
            <span className="text-[12px] font-bold text-emerald-600 mb-1 flex items-center">
              <ArrowDownIcon className="w-3 h-3 mr-0.5 stroke-2" />12%
            </span>
          </div>
        </div>

        {/* CSAT Score */}
        <div className="bg-slate-50 border border-slate-100 rounded-[20px] p-6 shadow-sm">
          <p className="text-[11px] font-extrabold text-slate-500 uppercase tracking-widest mb-3">CSAT Score</p>
          <div className="flex items-baseline gap-1">
            <h2 className="text-[36px] font-extrabold text-slate-900 tracking-tight leading-none">4.9</h2>
            <span className="text-[14px] font-bold text-slate-500">/ 5.0</span>
          </div>
        </div>

        {/* Total Resolved */}
        <div className="bg-slate-50 border border-slate-100 rounded-[20px] p-6 shadow-sm">
          <p className="text-[11px] font-extrabold text-slate-500 uppercase tracking-widest mb-3">Total Resolved</p>
          <div className="flex items-baseline gap-2">
            <h2 className="text-[36px] font-extrabold text-slate-900 tracking-tight leading-none">1,204</h2>
            <span className="text-[12px] font-bold text-slate-500">this month</span>
          </div>
        </div>

        {/* Pending Tickets */}
        <div className="bg-brand-50 border border-brand-100 rounded-[20px] p-6 shadow-sm">
          <p className="text-[11px] font-extrabold text-brand-600 uppercase tracking-widest mb-3">Pending Tickets</p>
          <div className="flex items-baseline gap-2">
            <h2 className="text-[36px] font-extrabold text-brand-600 tracking-tight leading-none">28</h2>
            <span className="text-[12px] font-bold text-brand-600">Urgent: 3</span>
          </div>
        </div>

      </div>

    </div>
  );
}